import { useState, useEffect, type ReactNode, useRef, useId } from 'react';
import { useStore } from 'zustand';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getPaginationRowModel,
  ColumnDef,
  VisibilityState,
  functionalUpdate,
  Row,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/cn';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from '@/components/ui/select';
import { usePaginationStore } from '@/store/use-pagination-store';
import useViewport from '@/hooks/use-viewport';
import { useShallow } from 'zustand/react/shallow';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface TableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  id?: string;
  loading?: boolean;
  isMarketPage?: boolean; // TODO: Remove this prop?
  disableDelay?: boolean;
  rowsPerPage?: number;
  hideInputPerPage?: boolean;
  isInfiniteScroll?: boolean;
  infiniteScrollEstimateRowSize?: number;
  infiniteScrollOverscan?: number;
  rowModelPagesOffset?: number;
  initialSorting?: SortingState;
  defaultsorting?: SortingState;
  columnVisibility?: VisibilityState;
  emptyComponent?: ReactNode;
  className?: string;
  paginationClassName?: string;
  leftGradientClassName?: string;
  rightGradientClassName?: string;
  bgColor?: string;
  onLoad?: (tableRef: HTMLDivElement) => void;
  onRowModel?: (rows: T[]) => void;
  onRowClick?: (row: T) => void;
  getRowClassName?: (row: T) => string;
}

const Table = <T,>(props: TableProps<T>) => {
  const {
    data,
    columns,
    id: forcedId,
    loading = false,
    isMarketPage = false,
    disableDelay,
    rowsPerPage,
    hideInputPerPage,
    isInfiniteScroll,
    infiniteScrollEstimateRowSize,
    infiniteScrollOverscan,
    rowModelPagesOffset,
    initialSorting,
    defaultsorting,
    columnVisibility,
    emptyComponent,
    className,
    paginationClassName,
    leftGradientClassName,
    rightGradientClassName,
    bgColor = 'bg-background',
    onLoad,
    onRowModel,
    onRowClick,
    getRowClassName,
  } = props;

  const { t } = useTranslation();

  const parentRef = useRef<HTMLDivElement>(null);
  const leftGradientRef = useRef<HTMLDivElement>(null);
  const rightGradientRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<SortingState>(
    initialSorting || defaultsorting || [],
  );
  const [isDelayed, setIsDelayed] = useState<boolean>(!!disableDelay);
  const isLoading = !isDelayed || loading;

  const generatedId = useId();

  const id = forcedId ?? generatedId;

  const pagination = useStore(
    usePaginationStore(id),
    useShallow((state) => state.pagination),
  );

  useViewport((viewport) => {
    if (isInfiniteScroll || viewport !== 'sm') {
      return;
    }

    usePaginationStore(id)
      .getState()
      .setPagination({
        pageSize: isMarketPage ? 20 : 5,
        pageIndex: 0,
      });
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination: isInfiniteScroll ? undefined : pagination,
      columnVisibility,
    },
    autoResetPageIndex: false,
    onSortingChange: (updater) => {
      const newSorting = functionalUpdate(updater, sorting);

      if (!newSorting.length && defaultsorting?.length) {
        setSorting(defaultsorting);
      } else {
        setSorting(newSorting);
      }
    },
    onPaginationChange: isInfiniteScroll
      ? undefined
      : (state) => usePaginationStore(id).getState().setPagination(state),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: isInfiniteScroll
      ? undefined
      : getPaginationRowModel(),
  });

  const rowVirtualizer = useVirtualizer({
    count: table.getRowModel().rows.length,
    overscan: infiniteScrollOverscan || 20,
    getScrollElement: () => parentRef.current,
    estimateSize: () => infiniteScrollEstimateRowSize || 36,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0;
  const gradientColor = `hsl(var(--${bgColor.replace('bg-', '')}))`;

  useEffect(() => {
    if (!onRowModel) {
      return;
    }

    if (isInfiniteScroll) {
      const visibleRows = rowVirtualizer
        .getVirtualItems()
        .map(
          (virtualRow) => table.getRowModel().rows[virtualRow.index]?.original,
        )
        .filter(Boolean);

      onRowModel(visibleRows);

      return;
    }

    if (rowModelPagesOffset) {
      const startIndex = Math.max(
        0,
        (pagination.pageIndex - rowModelPagesOffset) * pagination.pageSize,
      );
      const endIndex =
        (pagination.pageIndex + 1 + rowModelPagesOffset) * pagination.pageSize;

      const sortedRows = table
        .getSortedRowModel()
        .rows.map((row) => row.original);

      onRowModel(sortedRows.slice(startIndex, endIndex));

      return;
    }

    const currentRows = table.getRowModel().rows.map((row) => row.original);

    onRowModel(currentRows);
  }, [table.getRowModel().rows, onRowModel]);

  useEffect(() => {
    if (disableDelay) {
      return;
    }

    setTimeout(() => setIsDelayed(true), 300);
  }, [disableDelay]);

  useEffect(() => {
    if (!isInfiniteScroll && pagination.pageIndex + 1 > table.getPageCount())
      usePaginationStore(id)
        .getState()
        .setPagination((prev) => ({
          ...prev,
          pageIndex: 0,
        }));
  }, [id, pagination.pageSize, data]);

  useEffect(() => {
    if (isInfiniteScroll || rowsPerPage === undefined) {
      return;
    }

    table.setPageSize(rowsPerPage > 20 ? 20 : rowsPerPage);
  }, [rowsPerPage]);

  useEffect(() => {
    if (isLoading || !parentRef.current) return;
    onLoad?.(parentRef.current);
  }, [isLoading]);

  useEffect(() => {
    const update = () => {
      if (!parentRef.current) {
        return;
      }

      const { scrollLeft, scrollWidth, clientWidth } = parentRef.current;

      leftGradientRef.current?.classList.toggle('opacity-100', scrollLeft > 0);
      rightGradientRef.current?.classList.toggle(
        'opacity-100',
        scrollLeft + clientWidth < scrollWidth - 2,
      );
    };

    const parent = parentRef.current;
    let resizeObserver: any;

    if (!parent) {
      return;
    }

    update();
    parent.addEventListener('scroll', update);
    window.addEventListener('resize', update);

    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      const ResizeObserverCtor = (window as any).ResizeObserver;

      resizeObserver = new ResizeObserverCtor(() => {
        update();
      });

      resizeObserver.observe(parent);
    }

    return () => {
      parent.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      resizeObserver?.disconnect();
    };
  }, []);

  return (
    <div
      ref={parentRef}
      className={cn(
        'no-scrollbar flex h-full max-h-full w-full flex-col overflow-y-auto',
        !isInfiniteScroll && data.length > 5 && 'pb-12',
        className,
      )}
    >
      <div
        ref={leftGradientRef}
        className={cn(
          'pointer-events-none absolute bottom-0 left-0 top-0 z-20 w-28 origin-left opacity-0 transition-opacity duration-100 md:w-32',
          leftGradientClassName,
        )}
        style={{
          background: `linear-gradient(to left, transparent 0%, ${gradientColor} 70%, ${gradientColor} 100%)`,
        }}
      />
      <div
        ref={rightGradientRef}
        className={cn(
          'pointer-events-none absolute bottom-0 right-0 top-0 z-20 w-28 origin-right opacity-0 transition-opacity duration-100 md:w-32',
          rightGradientClassName,
        )}
        style={{
          background: `linear-gradient(to right, transparent 0%, ${gradientColor} 70%, ${gradientColor} 100%)`,
        }}
      />

      <table className="w-full table-auto">
        <thead className="sticky top-0 z-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={cn(bgColor)}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={cn(
                    'select-none py-3 pr-5 first:pl-5 last:pr-5 md:first:pl-8 md:last:pr-8',
                    header.column.getCanSort() && 'cursor-pointer',
                    // @ts-ignore
                    header.column.columnDef.meta?.width,
                  )}
                  {...{
                    onClick: header.column.getToggleSortingHandler(),
                  }}
                >
                  {header.isPlaceholder ? null : (
                    <div className="flex items-center gap-2">
                      <div className="text-base font-normal capitalize text-muted-foreground">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>

                      {header.column.getCanSort() && (
                        <div className="relative flex flex-col items-center justify-center">
                          <ChevronUp
                            size={12}
                            strokeWidth={3}
                            className={cn(
                              'relative top-[3px]',
                              header.column.getIsSorted() === 'asc'
                                ? 'text-foreground'
                                : 'text-muted-foreground',
                            )}
                          />

                          <ChevronDown
                            size={12}
                            strokeWidth={3}
                            className={cn(
                              'relative bottom-[2px]',
                              header.column.getIsSorted() === 'desc'
                                ? 'text-foreground'
                                : 'text-muted-foreground',
                            )}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {isLoading &&
            [...Array(3)].map((_, i) => (
              <tr key={i}>
                {table.getVisibleFlatColumns().map((col, i) => (
                  <td
                    key={i}
                    className={cn(
                      'group/skeleton py-3 pr-5 first:pl-5 last:pr-5 md:first:pl-8 md:last:pr-8',
                      //@ts-ignore
                      col.columnDef.meta?.width,
                    )}
                  >
                    <div className="h-4 w-[80%] animate-pulse rounded bg-vestgrey-800" />
                  </td>
                ))}
              </tr>
            ))}

          {!isLoading && data.length > 0 && (
            <>
              {isInfiniteScroll && paddingTop > 0 && (
                <tr style={{ height: `${paddingTop}px` }}>
                  <td colSpan={table.getVisibleFlatColumns().length} />
                </tr>
              )}

              {(isInfiniteScroll ? virtualRows : table.getRowModel().rows).map(
                (virtualRow) => {
                  const row = isInfiniteScroll
                    ? table.getRowModel().rows[virtualRow.index]
                    : (virtualRow as Row<T>);

                  return (
                    <tr
                      key={row.id}
                      className={cn(
                        'group border-b border-border text-lg text-foreground last:border-b-0 md:text-xl',
                        bgColor,
                        getRowClassName?.(row.original),
                      )}
                      onClick={() => onRowClick?.(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            'py-3 pr-5 first:pl-5 last:pr-5 md:first:pl-8 md:last:pr-8',
                            onRowClick &&
                              'cursor-pointer group-hover:bg-primary/5',
                            // @ts-ignore
                            cell.column.columnDef.meta?.width,
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                },
              )}

              {isInfiniteScroll && paddingBottom > 0 && (
                <tr style={{ height: `${paddingBottom}px` }}>
                  <td colSpan={table.getVisibleFlatColumns().length} />
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>

      {!isInfiniteScroll && data.length > 5 && (
        <div
          className={cn(
            'absolute bottom-0 z-30 flex h-12 w-full items-center gap-5 border-y border-border font-mono text-sm text-vestgrey-100',
            bgColor,
            paginationClassName,
          )}
        >
          {!hideInputPerPage && (
            <SelectGroup className="hidden h-full w-full max-w-max items-center pl-6 md:flex">
              <SelectLabel className="p-0 pr-4 font-mono text-sm font-normal text-vestgrey-100">
                {t('table.rowsPerPage')}
              </SelectLabel>

              <Select
                value={pagination.pageSize.toString()}
                onValueChange={(value) => table.setPageSize(parseInt(value))}
              >
                <SelectTrigger
                  id="per-page"
                  className="h-full max-w-14 rounded-none border-y-0 text-vestgrey-100"
                >
                  {table.getState().pagination.pageSize}
                </SelectTrigger>

                <SelectContent className="min-w-0 max-w-14 font-mono">
                  {[5, 10, 20].map((size) => (
                    <SelectItem
                      key={size}
                      value={size.toString()}
                      className="text-vestgrey-100"
                    >
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SelectGroup>
          )}

          <div className="flex h-full w-full flex-1 items-center justify-center">
            <button
              className="h-full px-3 hover:bg-vestgrey-800 hover:text-primary disabled:pointer-events-none"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {'<<'}
            </button>

            <button
              className="h-full px-3 hover:bg-vestgrey-800 hover:text-primary disabled:pointer-events-none"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {'< ' + t('table.prev')}
            </button>

            <span className="flex h-full items-center px-4">
              {t('table.pageOf', {
                current: table.getState().pagination.pageIndex + 1,
                total: table.getPageCount(),
              })}
            </span>

            <button
              className="h-full px-3 hover:bg-vestgrey-800 hover:text-primary disabled:pointer-events-none"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {t('table.next') + ' >'}
            </button>

            <button
              className="h-full px-3 hover:bg-vestgrey-800 hover:text-primary disabled:pointer-events-none"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
            >
              {'>>'}
            </button>
          </div>
        </div>
      )}

      {!isLoading && data.length === 0 && emptyComponent && (
        <div className="flex flex-1 items-center justify-center">
          {emptyComponent}
        </div>
      )}
    </div>
  );
};

export default Table;
